import React from 'react';
import { Box, Typography, makeStyles } from '@material-ui/core';
import { fade } from '@material-ui/core/styles/colorManipulator';
import clsx from 'clsx';
import moment from 'moment-timezone';
import { formatLicenceStateLabel } from './licenceStateUtils';

const ALERT_STATUSES = new Set(['blocked', 'suspended']);

const useStyles = makeStyles((theme) => ({
  root: {
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(1.5),
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(0.75),
  },
  // Tight vertical rhythm between label/value rows
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.75),
    minHeight: 22,
  },
  rowLast: {
    marginBottom: 0,
  },
  fieldLabel: {
    flexShrink: 0,
    lineHeight: 1.2,
    color: theme.palette.text.secondary,
  },
  fieldValue: {
    textAlign: 'right',
    lineHeight: 1.3,
    wordBreak: 'break-word',
    fontWeight: 700,
    color: theme.palette.primary.main,
  },
  fieldValueExpired: {
    color: theme.palette.secondary.main,
  },
  // Sharp full-bleed plate band (edge-to-edge of card); extra gap above/below
  plateBanner: {
    marginTop: theme.spacing(0.25),
    marginBottom: theme.spacing(1),
    marginLeft: theme.spacing(-1.5),
    marginRight: theme.spacing(-1.5),
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(1.5),
    borderRadius: 0,
    textAlign: 'center',
  },
  plateBannerOk: {
    backgroundColor: fade(theme.palette.primary.main, 0.1),
  },
  plateBannerAlert: {
    backgroundColor: fade(theme.palette.secondary.main, 0.1),
  },
  plateLabel: {
    display: 'block',
    marginBottom: 2,
    lineHeight: 1.2,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    fontWeight: 600,
  },
  plateValue: {
    lineHeight: 1.25,
    wordBreak: 'break-word',
    fontWeight: 700,
    fontSize: 18,
  },
  plateTextOk: {
    color: theme.palette.primary.main,
  },
  plateTextAlert: {
    color: theme.palette.secondary.main,
  },
}));

/** View-only plate band: red for blocked/suspended only (expiry does not tint the plate). */
const isPlateAlert = (status) => ALERT_STATUSES.has(status);

/** True when expiration_date is a valid moment before now. */
const isExpirationPast = (expirationDate) => {
  if (!expirationDate) {
    return false;
  }
  const date = moment(expirationDate);
  return date.isValid() && date.isBefore(moment());
};

const formatDisplayValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  return String(value);
};

const formatDateValue = (value) => {
  if (!value) {
    return '—';
  }
  const date = moment(value);
  return date.isValid() ? date.format('YYYY-MM-DD') : '—';
};

const ViewRow = ({
  label,
  children,
  classes,
  last = false,
  valueClassName,
}) => (
  <Box className={`${classes.row}${last ? ` ${classes.rowLast}` : ''}`}>
    <Typography variant="caption" className={classes.fieldLabel}>
      {label}
    </Typography>
    <Typography
      variant="body2"
      component="div"
      className={clsx(classes.fieldValue, valueClassName)}
    >
      {children}
    </Typography>
  </Box>
);

const PermitCardView = ({ values }) => {
  const classes = useStyles();
  const status = String(values?.status || '')
    .toLowerCase()
    .trim();
  const plateAlert = isPlateAlert(status);
  const plateTone = plateAlert ? classes.plateTextAlert : classes.plateTextOk;
  const expirationPast = isExpirationPast(values?.expiration_date);

  return (
    <Box className={classes.root}>
      <ViewRow label="Issue Date:" classes={classes}>
        {formatDateValue(values?.issue_date)}
      </ViewRow>
      <ViewRow
        label="Expiration Date:"
        classes={classes}
        valueClassName={expirationPast ? classes.fieldValueExpired : undefined}
      >
        {formatDateValue(values?.expiration_date)}
      </ViewRow>
      <Box
        className={clsx(
          classes.plateBanner,
          plateAlert ? classes.plateBannerAlert : classes.plateBannerOk
        )}
      >
        <Typography
          variant="caption"
          className={clsx(classes.plateLabel, plateTone)}
        >
          License Plate
        </Typography>
        <Typography
          component="div"
          className={clsx(classes.plateValue, plateTone)}
        >
          {formatDisplayValue(values?.licence_plate)}
        </Typography>
      </Box>
      <ViewRow label="Licence State:" classes={classes}>
        {formatLicenceStateLabel(values?.licence_state)}
      </ViewRow>
      <ViewRow label="Make:" classes={classes}>
        {formatDisplayValue(values?.make)}
      </ViewRow>
      <ViewRow label="Model:" classes={classes}>
        {formatDisplayValue(values?.model)}
      </ViewRow>
      <ViewRow label="Color:" classes={classes} last>
        {formatDisplayValue(values?.color)}
      </ViewRow>
    </Box>
  );
};

export default PermitCardView;
